/**
 * Referral statistics — the FOURTH separate stats contract (CLAUDE.md §21.1).
 *
 * computeReferralStats(referrals) returns four figures for the Adviser
 * dashboard's referral counter block. It operates ONLY over referral rows
 * and, like every other stat function in the app, excludes test records
 * (is_test = true, §33) at source.
 *
 * SEPARATION, BOTH WAYS: this module imports nothing from
 * dashboardStats.js, bonusCalculator.js, bonusClock.js or slaCalculator.js,
 * and none of those (nor computePrepStats in prepRequests.js, nor
 * useAchievements) imports anything from here. Its only imports are the pure
 * referral vocabulary / predicate module (utils/referrals.js) and the SAST
 * date formatter in utils/dateHelpers.js, which counts nothing. Referral
 * figures never feed a file, prep or review statistic, the bonus counter,
 * or any SLA calculation.
 */

import { isReferralOverdue, isReferralFeedbackOwed } from './referrals';
import { getTodaySASTDateString } from './dateHelpers';

const ACTIVE_STATUSES = ['inbox', 'in_progress', 'paused'];

/**
 * @param {Array}  referrals — camelCase rows from referralsApi
 * @param {Date}   [now]     — injectable clock (tests); defaults to now
 * @returns {{ active: number, overdue: number, feedbackOwed: number, convertedThisYear: number }}
 *
 *   active            — inbox + in_progress + paused
 *   overdue           — inbox / in_progress whose next_action_on is before
 *                       today (SAST); paused cards are not "overdue", their
 *                       resume date is a separate cue on the attention strip
 *   feedbackOwed      — completed with an outcome and feedback_given_at null
 *   convertedThisYear — completed with outcome 'converted' whose
 *                       completed_at falls in the current SAST calendar year
 */
export function computeReferralStats(referrals, now = new Date()) {
  const list = (Array.isArray(referrals) ? referrals : []).filter(
    (r) => r && r.isTest !== true
  );
  const thisYear = getTodaySASTDateString(now).slice(0, 4);

  let active = 0;
  let overdue = 0;
  let feedbackOwed = 0;
  let convertedThisYear = 0;

  for (const r of list) {
    const status = r.pipelineStatus;
    if (ACTIVE_STATUSES.includes(status)) active += 1;
    if ((status === 'inbox' || status === 'in_progress') && isReferralOverdue(r, now)) overdue += 1;
    if (isReferralFeedbackOwed(r)) feedbackOwed += 1;
    if (
      status === 'completed' &&
      r.outcome === 'converted' &&
      typeof r.completedAt === 'string' &&
      sastYearOf(r.completedAt) === thisYear
    ) {
      convertedThisYear += 1;
    }
  }

  return { active, overdue, feedbackOwed, convertedThisYear };
}

/** 'YYYY' of an ISO timestamp in Africa/Johannesburg, or null. */
function sastYearOf(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return getTodaySASTDateString(d).slice(0, 4);
}

/**
 * Number of DISTINCT referrals needing attention for the unified statistics
 * bar's Referrals badge (CLAUDE.md §37): overdue OR feedback owed OR both,
 * each referral counted once. Uses exactly the same predicates as
 * computeReferralStats's Overdue and Feedback Owed figures, and excludes
 * test records at source.
 */
export function countReferralsNeedingAttention(referrals, now = new Date()) {
  const list = (Array.isArray(referrals) ? referrals : []).filter(
    (r) => r && r.isTest !== true
  );
  let count = 0;
  for (const r of list) {
    const status = r.pipelineStatus;
    const overdue = (status === 'inbox' || status === 'in_progress') && isReferralOverdue(r, now);
    if (overdue || isReferralFeedbackOwed(r)) count += 1;
  }
  return count;
}
